import { DiscoverySourceType, OnboardingIntentType } from '@orbit/api-types'

export type SurveyStep = 'intent' | 'discovery'

export interface IntentOption {
	id: OnboardingIntentType
	title: string
	description: string
	exclusive?: boolean
}

// Ids come from the shared enum so the survey, the API and the admin onboarding
// view agree on their spelling; the mobile apps hold their own copies, so a
// change here is a full-stack rename. Copy stays local — the enum owns the ids.
export const INTENT_OPTIONS: IntentOption[] = [
	{ id: 'itinerary', title: 'Itinerary', description: 'Plan trips on a shared timeline' },
	{ id: 'ideas', title: 'Ideas', description: 'Save maybes, add them to your itinerary later' },
	{ id: 'expenses', title: 'Expense Tracking', description: 'Log costs and settle up easily' },
	{ id: 'exploring', title: "I'm just exploring", description: 'Take a look around first', exclusive: true },
]

export interface DiscoveryOption {
	value: DiscoverySourceType
	label: string
}

// AppStoreSearch is deliberately omitted on web (see CONTEXT.md: Discovery source).
export const DISCOVERY_OPTIONS: DiscoveryOption[] = [
	{ value: 'FriendOrFamily', label: 'A friend or family member' },
	{ value: 'SocialMedia', label: 'Social media' },
	{ value: 'WebSearch', label: 'Google or web search' },
	{ value: 'Other', label: 'Other' },
]

export const toggleIntent = (selected: string[], id: string): string[] => {
	const option = INTENT_OPTIONS.find(o => o.id === id)
	if (!option) return selected
	if (option.exclusive) {
		return selected.includes(id) ? [] : [id]
	}
	const withoutExclusive = selected.filter(
		s => !INTENT_OPTIONS.some(o => o.id === s && o.exclusive),
	)
	return withoutExclusive.includes(id)
		? withoutExclusive.filter(s => s !== id)
		: [...withoutExclusive, id]
}

// --- Survey state machine (the modal is a thin shell over these transitions) ---

export interface SurveyState {
	step: SurveyStep
	selectedIntents: string[]
	selectedSource: DiscoverySourceType | null
}

export const initialSurveyState: SurveyState = {
	step: 'intent',
	selectedIntents: [],
	selectedSource: null,
}

export interface SurveyAnalyticsEvent {
	eventType: string
	data: Record<string, unknown>
}

export interface SurveyTransition {
	state: SurveyState
	events: SurveyAnalyticsEvent[]
	// true once the survey is over (answered or skipped): clear the marker and close
	done: boolean
}

export const stepTitle = (step: SurveyStep): string =>
	step === 'intent' ? 'How are you planning on using Orbit?' : 'How did you hear about Orbit?'

export const ctaLabel = (step: SurveyStep): string => (step === 'intent' ? 'Continue' : 'Done')

export const selectIntent = (state: SurveyState, id: string): SurveyState => ({
	...state,
	selectedIntents: toggleIntent(state.selectedIntents, id),
})

export const selectSource = (state: SurveyState, source: DiscoverySourceType): SurveyState => ({
	...state,
	selectedSource: source,
})

export const canAdvance = (state: SurveyState): boolean =>
	state.step === 'intent' ? state.selectedIntents.length > 0 : state.selectedSource != null

export const advance = (state: SurveyState): SurveyTransition => {
	if (!canAdvance(state)) {
		return { state, events: [], done: false }
	}
	if (state.step === 'intent') {
		return {
			state: { ...state, step: 'discovery' },
			events: [{ eventType: 'onboarding_intent', data: { selectedIntents: state.selectedIntents } }],
			done: false,
		}
	}
	return {
		state,
		events: [{ eventType: 'onboarding_discovery_source', data: { source: state.selectedSource } }],
		done: true,
	}
}

// Skipping ends the whole survey and never re-asks; steps already submitted keep their events.
export const skip = (state: SurveyState): SurveyTransition => ({
	state,
	events: [{ eventType: 'onboarding_survey_skipped', data: { step: state.step } }],
	done: true,
})

// --- Pending marker ---

// Written when a login response reports isNewUser, cleared on answer or skip, so the
// survey survives refreshes (the app boots from a stored session without a login call).
// Stores the new User's id: on a shared browser, a different account logging in later
// must never be asked (or have its answers attributed to) someone else's survey.
const PENDING_KEY = 'orbit.onboardingSurvey.pending'

export const isSurveyPending = (userId: number | undefined): boolean => {
	if (userId == null) return false
	try {
		return localStorage.getItem(PENDING_KEY) === String(userId)
	} catch {
		return false
	}
}

export const markSurveyPendingIfNew = (isNewUser: boolean | undefined, userId: number): void => {
	if (!isNewUser) return
	try {
		localStorage.setItem(PENDING_KEY, String(userId))
	} catch {
		// Best-effort: losing the marker only costs us one survey answer
	}
}

export const clearSurveyPending = (): void => {
	try {
		localStorage.removeItem(PENDING_KEY)
	} catch {
		// Best-effort: losing the marker only costs us one survey answer
	}
}
